// SPDX-License-Identifier: Elastic-2.0
/**
 * SuggestionApprovalModal — confirmation surface for promoting a
 * pending AI suggestion into a real routine (T-025).
 *
 * Per functional spec § 6: shows a "Mirlai noticed" eyebrow, the
 * plain-language description verbatim, a trigger summary, an action
 * summary, and Approve / Decline CTAs. On approve success: closes the
 * modal, fires a "Routine created" toast, and (if the response carries
 * a `routine_id`) navigates to `/routines/{id}`. On decline success:
 * closes + "Dismissed" toast. On either failure: inline `<Banner>` so
 * the user can retry without losing context.
 *
 * Two render modes (controlled via the `mode` prop):
 *
 *   - `"approve"` (default): full footer with Yes / No buttons + the
 *     mutation plumbing described above. This is the canonical surface
 *     opened from `<SuggestionStrip />`.
 *   - `"view"`: read-only — the footer renders just a "Close" button.
 *     Used when the user clicks "More info" on a `<SuggestionCard />`
 *     in the suggestions list (T-027) and we just want to surface the
 *     details without forcing a decision.
 *
 * Hand-off contract with DashboardPage (T-020):
 *   DashboardPage owns the `selectedSuggestion` state, passes it +
 *   `onClose` to this modal, and passes `setSelectedSuggestion` as the
 *   `onAccept` prop of `<SuggestionStrip />`. We don't read suggestions
 *   from the store inside this modal — the parent picks which one is
 *   open. This keeps the modal trivially mockable in tests.
 *
 * Why we don't call `useNavigate` at the module top: tests render this
 * modal both inside and outside a router (see formatProposedRoutine
 * tests for the lone-component pattern). The hook is mandatory inside
 * `react-router-dom`'s tree but the modal is also exercised by
 * `DashboardPage` test (which wraps in `<MemoryRouter>`). Using the
 * hook unconditionally is fine here — every production caller mounts
 * the modal under the dashboard router; tests that don't need
 * navigation simply assert no nav happened.
 */
import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { Suggestion } from "@/api/gateway/models/Suggestion";
import { Banner } from "@/components/Banner/Banner";
import { Button } from "@/components/Button";
import { MirlaiMark } from "@/components/MirlaiMark/MirlaiMark";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast/useToast";
import { ROUTES } from "@/routing/routes";

import { formatProposedRoutine, NO_DETAILS_KEY } from "./formatProposedRoutine";
import {
  confidenceLabelKey,
  useApproveSuggestion,
  useDismissSuggestion,
} from "./useSuggestions";
import styles from "./SuggestionApprovalModal.module.css";

export type SuggestionApprovalModalMode = "approve" | "view";

export interface SuggestionApprovalModalProps {
  /**
   * The suggestion to render. When `null` (or `open` is `false`) the
   * modal returns `null` — no portal, no listeners.
   */
  suggestion: Suggestion | null;
  /**
   * Whether the modal should be visible. Mirrors the `Modal` primitive
   * for symmetry; the parent typically derives this from
   * `selectedSuggestion !== null`.
   */
  open: boolean;
  /** Called when the modal requests close (Esc, backdrop, X, action). */
  onClose: () => void;
  /**
   * Render mode. `"approve"` (default) shows the Yes / No footer +
   * wires the mutations. `"view"` hides the action buttons — used by
   * "More info" flows that only need to surface the details.
   */
  mode?: SuggestionApprovalModalMode;
}

/**
 * Translate the trigger / action sentence, swapping in the i18n
 * no-details copy when the formatter signalled a fallback. Kept as a
 * tiny helper so both rows share the same logic.
 */
function resolveSentence(
  text: string,
  isFallback: boolean,
  t: (key: string) => string,
): string {
  return isFallback ? t(NO_DETAILS_KEY) : text;
}

export function SuggestionApprovalModal({
  suggestion,
  open,
  onClose,
  mode = "approve",
}: SuggestionApprovalModalProps): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const approve = useApproveSuggestion();
  const dismiss = useDismissSuggestion();

  // Local mutation-error state so we can render an inline `<Banner>` and
  // keep the modal open for retry. We deliberately don't lean on the
  // mutation's `isError` flag because we want to clear the banner the
  // moment the user clicks again (so they don't see a stale red banner
  // while the new attempt is in flight).
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // When `open` flips false OR there's no suggestion to render, bail
  // before hitting hooks-dependent rendering. The earlier returns are
  // safe because all hooks above are unconditional.
  if (!open || suggestion === null) {
    return null;
  }

  const summary = formatProposedRoutine(suggestion.proposed_routine);
  const triggerText = resolveSentence(summary.trigger, summary.fallback.trigger, t);
  const actionText = resolveSentence(summary.action, summary.fallback.action, t);
  const confidenceLabel = t(confidenceLabelKey(suggestion.confidence));

  const busy = approve.isPending || dismiss.isPending;
  const dismissable = !busy; // Lock Esc / backdrop while a mutation is in flight.

  const handleClose = (): void => {
    if (busy) return; // Defensive — `dismissable` already gates Esc / backdrop.
    setErrorMessage(null);
    onClose();
  };

  const handleApprove = (): void => {
    setErrorMessage(null);
    approve.mutate(suggestion.id, {
      onSuccess: (response) => {
        showToast({
          kind: "success",
          message: t("suggestion_modal.approve_success"),
        });
        onClose();
        if (response?.routine_id) {
          navigate(`${ROUTES.ROUTINES}/${response.routine_id}`);
        }
      },
      onError: () => {
        setErrorMessage(t("suggestion_modal.approve_error"));
      },
    });
  };

  const handleDecline = (): void => {
    setErrorMessage(null);
    dismiss.mutate(suggestion.id, {
      onSuccess: () => {
        showToast({
          kind: "info",
          message: t("suggestion_modal.dismiss_success"),
        });
        onClose();
      },
      onError: () => {
        setErrorMessage(t("suggestion_modal.dismiss_error"));
      },
    });
  };

  const footer: ReactNode =
    mode === "view" ? (
      <Button
        variant="secondary"
        size="sm"
        onClick={handleClose}
        data-testid="suggestion-modal-close"
      >
        {t("suggestion_modal.close")}
      </Button>
    ) : (
      <>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDecline}
          disabled={busy}
          data-testid="suggestion-modal-decline"
        >
          {t("suggestion_modal.decline")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleApprove}
          disabled={busy}
          data-testid="suggestion-modal-approve"
        >
          {t("suggestion_modal.approve")}
        </Button>
      </>
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("suggestion_modal.eyebrow")}
      size="md"
      dismissable={dismissable}
      footer={footer}
    >
      <div className={styles.body} data-testid="suggestion-approval-modal">
        <div className={styles.eyebrow}>
          <MirlaiMark size={14} />
          <span>{t("suggestions.mirlai_noticed")}</span>
          <span
            className={styles.confidencePill}
            data-testid="suggestion-modal-confidence"
          >
            {confidenceLabel}
          </span>
        </div>

        <p className={styles.description} data-testid="suggestion-modal-description">
          {suggestion.description}
        </p>

        <div className={styles.section}>
          <h3 className={styles.sectionHeading}>{t("suggestion_modal.when_label")}</h3>
          <p
            className={[
              styles.sectionText,
              summary.fallback.trigger ? styles.sectionTextGeneric : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-testid="suggestion-modal-trigger"
          >
            {triggerText}
          </p>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionHeading}>{t("suggestion_modal.action_label")}</h3>
          <p
            className={[
              styles.sectionText,
              summary.fallback.action ? styles.sectionTextGeneric : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-testid="suggestion-modal-action"
          >
            {actionText}
          </p>
        </div>

        {errorMessage ? (
          <div className={styles.errorSlot} data-testid="suggestion-modal-error">
            <Banner kind="error" message={errorMessage} />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export default SuggestionApprovalModal;
